// SAMPLE USAGE

// import { useSessionFeedback } from '@/providers/SessionFeedbackProvider';

// function MyComponent() {
//   const { open } = useSessionFeedback();

//   const handleFeedback = () => {
//     open({
//       feedbackType: 'CONTENT',
//       contentType: 'FLASHCARDS'
//     });
//   };

//   return <button onClick={handleFeedback}>Give Feedback</button>;
// }

import { useEffect, useRef, useState } from 'react';
import { Modal, ModalBody, ModalFooter } from '@/components/ui/modal';
import { capture } from '../../../../utils/analytics';
import { hapticsImpactLight } from '../../../../utils/haptics';
import { enqueueSnackbar } from 'notistack';
import { Capacitor } from '@capacitor/core';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { submitTestimonialInterest } from '@/services/feedback';

interface SessionFeedbackProps {
  open: boolean;
  onClose: () => void;
  feedbackType: 'EXPERIENCE' | 'CONTENT';
  contentType?: 'QBANK' | 'FLASHCARDS' | 'VIDEOS';
}

const SessionFeedback: React.FC<SessionFeedbackProps> = ({
  open,
  onClose,
  feedbackType,
  contentType,
}) => {
  const [feedbackRating, setFeedbackRating] = useState<number | undefined>(
    undefined
  );

  const [comments, setComments] = useState<string>('');
  // ORA-771: a 5/5 overall-experience rating offers a "use this as a
  // testimonial" checkbox. When it is checked, Submit first emails the team
  // (so Jake can reach out) and only then records the rating and closes.
  const [wantsTestimonial, setWantsTestimonial] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasTestimonialError, setHasTestimonialError] = useState(false);
  // Bumped on every opening so a submit that was still in flight when the
  // modal closed can't act on a newer prompt.
  const submitAttemptRef = useRef(0);

  useEffect(() => {
    if (open) {
      // The provider keeps this mounted between prompts, so start each one fresh.
      setFeedbackRating(undefined);
      setComments('');
      setWantsTestimonial(false);
      setIsSubmitting(false);
      setHasTestimonialError(false);
      submitAttemptRef.current += 1;
    }
  }, [open]);

  const shouldOfferTestimonial =
    feedbackType === 'EXPERIENCE' && feedbackRating === 5;

  // The checkbox keeps its value if the rating moves away from 5, but it only
  // counts while the offer is actually showing.
  const shouldSendTestimonial = shouldOfferTestimonial && wantsTestimonial;

  const handleSubmitFeedback = async () => {
    if (!feedbackRating || !feedbackType || isSubmitting) return;

    const trimmedComment = comments.trim();

    const platformProps = {
      isiOSApp: Capacitor.getPlatform() === 'ios',
      isAndroidApp: Capacitor.getPlatform() === 'android',
    };

    if (shouldSendTestimonial) {
      const attempt = submitAttemptRef.current;
      setIsSubmitting(true);
      setHasTestimonialError(false);

      try {
        await submitTestimonialInterest({
          rating: feedbackRating,
          comment: trimmedComment || undefined,
        });
      } catch (error) {
        if (attempt !== submitAttemptRef.current) return;
        console.error(error);
        setHasTestimonialError(true);
        setIsSubmitting(false);
        return;
      }

      if (attempt !== submitAttemptRef.current) return;
      setIsSubmitting(false);

      capture(
        'user_opted_in_testimonial',
        {
          rating: feedbackRating,
          hasComment: Boolean(trimmedComment),
          ...platformProps,
        },
        { withExam: true }
      );
    }

    capture(
      'user_rated_session',
      {
        rating: feedbackRating,
        feedbackType: feedbackType,
        contentType: feedbackType === 'CONTENT' ? contentType : 'N/A',
        comments: comments,
        requestedTestimonial: shouldSendTestimonial,
        ...platformProps,
      },
      { withExam: true }
    );

    enqueueSnackbar('Thank you for your feedback!', {
      autoHideDuration: 3000,
    });

    onClose();
  };

  const renderQuestion = (feedbackType: 'EXPERIENCE' | 'CONTENT') => {
    if (feedbackType === 'EXPERIENCE') {
      return (
        <>
          How has your <u>overall experience</u> with Ora been?
        </>
      );
    } else if (feedbackType === 'CONTENT' && contentType === 'VIDEOS') {
      return <>How was the quality of that video?</>;
    } else {
      return <>How was the content of that session?</>;
    }
  };

  const renderRatingScale = (rating: number) => {
    if (rating === 1) {
      return <>Very Poor</>;
    }
    if (rating === 3) {
      return <>Neutral</>;
    }
    if (rating === 5) {
      return <>Excellent</>;
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      name="session-feedback"
      blurs={false}
      className="modal--glass"
    >
      <ModalBody className="modal_content">
        <h2 className="!mt-0">{renderQuestion(feedbackType)}</h2>
        <div className="rating-wrapper" role="radiogroup" aria-label="Rating">
          {[1, 2, 3, 4, 5].map((rating) => (
            <div
              className="rating-button-wrapper"
              key={'feedback-rating-' + rating}
            >
              <button
                className={`button button--session-feedback-rating ${
                  feedbackRating === rating ? 'is-selected' : ''
                }`}
                onClick={() => setFeedbackRating(rating)}
                tabIndex={0}
                aria-label={'Rating ' + rating}
                role="radio"
                aria-checked={feedbackRating === rating}
              >
                <span>{rating}</span>
              </button>
              <div className="rating-scale">{renderRatingScale(rating)}</div>
            </div>
          ))}
        </div>
        {feedbackRating && (
          <div className="comments-wrapper">
            <div className="comments-wrapper_header">
              <label htmlFor="comments">Comments</label>
              <div>Optional</div>
            </div>
            <Textarea
              context="modal"
              id="comments"
              aria-label="Comments"
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Add any comments or suggestions..."
            />
          </div>
        )}
        {shouldOfferTestimonial && (
          <div
            className="relative z-10 mt-6 w-full text-left"
            data-testid="testimonial-prompt"
          >
            <Checkbox
              context="modal"
              checked={wantsTestimonial}
              disabled={isSubmitting}
              onChange={(e) => setWantsTestimonial(e.target.checked)}
              label="Use this as a testimonial?"
            />
            {hasTestimonialError && (
              <p className="mt-2 text-xs text-modal-fg" role="alert">
                We couldn&apos;t send your testimonial request. Please try
                again, or uncheck the box to submit without it.
              </p>
            )}
          </div>
        )}
        <div className="blur-purple"></div>
        <div className="blur-pink"></div>
      </ModalBody>
      <ModalFooter className="modal_actions !gap-2">
        <Button
          disabled={!feedbackRating || isSubmitting}
          onClick={handleSubmitFeedback}
          tabIndex={0}
          aria-label="Submit Feedback"
          context="modal"
          variant="outline"
          className="w-full"
        >
          {isSubmitting ? 'Submitting…' : 'Submit'}
        </Button>
        <Button
          className="w-full"
          variant="ghost"
          context="modal"
          size="sm"
          onClick={() => {
            hapticsImpactLight();
            onClose();

            capture(
              'user_skipped_session_feedback',
              {
                feedbackType: feedbackType,
              },
              { withExam: true }
            );
          }}
          tabIndex={0}
          aria-label="Skip Feedback"
        >
          Skip
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default SessionFeedback;
